import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { getSurvey, sendSurvey } from "../../services";
import {
  Box,
  Button,
  Container,
  Grid,
  Paper,
  ThemeProvider,
} from "@mui/material";
import Logo from "../../components/Logo";
import { useForm } from "react-hook-form";
import SurveyField from "../../components/form/SurveyField";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import DialogError from "../../components/DialogError";
import Success from "../Success";
import { LoadingButton } from "@mui/lab";
import bg1 from "assets/bg_survey_01.svg";
import bg2 from "assets/bg_survey_02.svg";
import bg1Diamond from "assets/diamond/bg_survey_01.svg";
import bg2Diamond from "assets/diamond/bg_survey_02.svg";
import theme, { themeDiamond } from "../../theme";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import { isEnableCapcha } from "helpers/commonFunctions";

const Survey = ({}) => {
  const { executeRecaptcha } = useGoogleReCaptcha();
  const { code, type, id } = useParams();
  const [data, setData] = useState();
  const [error, setError] = useState();
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);

  const searchParams = new URLSearchParams(window.location.search);
  const lang = searchParams.get("lang");

  const schema = useMemo(() => {
    if (data) {
      const msgMax2000 =
        lang === "en" ? "Maximum 2000 characters" : "Tối đa 2000 ký tự";
      const msgRequired =
        lang === "en" ? "This field is required" : "Không được để trống";
      const msgSelectAnswer =
        lang === "en" ? "Please select an answer" : "Vui lòng chọn đáp án";

      const validate = {};
      data.questionList.forEach((question) => {
        if (question.type === "TEXT") {
          validate[question.questionId] = yup
            .array()
            .test("", msgMax2000, function (value) {
              if (value) {
                return value[0].text.length <= 2000;
              }
              return true;
            });
        } else {
          question.answerList.forEach((answer) => {
            if (answer.hasText) {
              validate[question.questionId] = yup.array().of(
                yup.object().shape({
                  text: yup.string().test("", msgMax2000, function (value) {
                    if (value) {
                      return value.length <= 2000;
                    }
                    return true;
                  }),
                })
              );
            }
          });
        }
        if (question.require) {
          if (question.type === "TEXT") {
            validate[question.questionId] =
              validate[question.questionId].required(msgRequired);
          } else {
            validate[question.questionId] = (
              validate[question.questionId] || yup.array()
            )
              .required(msgSelectAnswer)
              .min(1, msgSelectAnswer);
          }
        }
      });
      return yup.object(validate).required();
    }
  }, [data, lang]);
  const {
    control,
    setFocus,
    formState: { errors },
    handleSubmit,
  } = useForm({
    mode: "all",
    resolver: yupResolver(schema),
  });
  React.useEffect(() => {
    const firstError = Object.keys(errors).reduce((field, a) => {
      return !!errors[field] ? field : a;
    }, null);

    if (firstError) {
      setFocus(firstError);
    }
  }, [errors, setFocus]);
  useEffect(() => {
    if (code && id) {
      getSurvey(code, id)
        .then((data) => {
          if (data) {
            setData(data);
          }
        })
        .catch((e) => {
          if (e.response.data.errorCode === 500) {
            setSuccess(true);
          }
        });
    }
  }, []);
  const handleOnSubmit = async (value) => {
    let token;
    if (isEnableCapcha()) {
      if (!executeRecaptcha) {
        console.error("Execute recaptcha not yet available");
        return;
      }
      token = await executeRecaptcha("submit");
    }
    setLoading(true);
    const responseList = Object.keys(value).map((key) => ({
      questionId: Number(key),
      answerList: value[key],
    }));
    sendSurvey(code, id, {
      captcha: token,
      responseList: responseList,
    })
      .then(() => {
        setLoading(false);
        setSuccess(true);
      })
      .catch((e) => {
        setLoading(false);
        setError(e.response.data.message);
      });
  };
  if (success) {
    return (
      <Success
        lang={lang}
        content={
          lang === "en"
            ? `Thank you for taking the survey! VPBank looks forward to your feedback in the future so we can continue enhancing your experience.`
            : `Cảm ơn Quý khách đã thực hiện khảo sát! VPBank rất mong muốn nhận được chia sẻ, đóng góp ý
                    kiến của Quý khách trong tương lai để chúng tôi có thể tiếp tục mang đến những trải nghiệm
                    tuyệt vời hơn`
        }
      />
    );
  }
  if (!data) {
    return (
      <DialogError open={error} onClose={() => setError("")} message={error} />
    );
  }
  return (
    <ThemeProvider theme={type === "d" ? themeDiamond : theme}>
      <Box sx={{ background: "#f7f7f7", height: "100vh", overflow: "auto" }}>
        <Container className="pt-8 pb-20 relative">
          <Paper
            component="form"
            onSubmit={handleSubmit(handleOnSubmit)}
            elevation={1}
            className="bg-white py-6 px-[3%] !rounded-lg relative"
          >
            <img
              src={type === "d" ? bg1Diamond : bg1}
              alt=""
              className="absolute top-[180px] right-0 z-0 opacity-60"
            />
            <img
              src={type === "d" ? bg2Diamond : bg2}
              alt=""
              className="absolute bottom-[80px] left-0 z-0 opacity-60"
            />
            <Logo type={type} />

            <div className="relative z-1">
              <div
                className="mt-6 whitespace-pre-line text-center font-semibold text-[22px]"
                style={{
                  background:
                    type === "d"
                      ? "-webkit-linear-gradient(90deg, rgb(124, 71, 40) 0%, rgb(239, 183, 100) 100%, rgb(239, 183, 100) 100%) text"
                      : "-webkit-linear-gradient(225deg, rgb(0, 183, 79) -22.41%, rgb(29, 66, 137) 108.33%) text",
                  WebkitTextFillColor: "transparent",
                }}
              >
                {data?.title}
              </div>
              <div className="mt-2 px-6 whitespace-pre-line text-center">
                {data.description}
              </div>
              <div className="px-[6%] py-6">
                <Grid container spacing={4}>
                  {data.questionList.map((question, index) => (
                    <SurveyField
                      index={index}
                      control={control}
                      question={question}
                      type={type}
                      key={question.questionId}
                    />
                  ))}
                </Grid>
                <div className="flex justify-center mt-8">
                  <LoadingButton
                    loading={loading}
                    className="!rounded-full !px-6 !py-3"
                    type="submit"
                    sx={{
                      background:
                        type === "d"
                          ? "linear-gradient(90deg, rgb(124, 71, 40) 0%, rgb(239, 183, 100) 100%, rgb(239, 183, 100) 100%)"
                          : "linear-gradient(90deg, rgb(29, 66, 137) 0%, rgb(0, 183, 79) 76.56%)",
                    }}
                    variant="contained"
                    color="primary"
                  >
                    {lang === "en" ? "Submit" : "Gửi thông tin"}
                  </LoadingButton>
                </div>
              </div>
            </div>
          </Paper>
        </Container>
        <DialogError
          open={error}
          onClose={() => setError("")}
          message={error}
        />
      </Box>
    </ThemeProvider>
  );
};

export default Survey;
